// Discipline Section Component - Enhanced with encrypted query parameter support for orgId and appId
// Now retrieves orgId and appId from URL query parameters (encrypted) with fallback to URL params
// No localStorage fallback - parameters must be provided via URL
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
    Box,
    Paper,
    Typography,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Alert,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    CircularProgress,
    Chip
} from '@mui/material';
import {
    Delete as DeleteIcon,
    Add as AddIcon,
    AddCircle as AddCircleIcon,
    CloudUpload as BulkUploadIcon,
    Visibility as ViewIcon} from '@mui/icons-material';
import TestingLaboratoryService from '../../../services/testinglaboratoryService';
import { createEncryptedQueryString, decryptText, decryptAppId, encryptText, encryptAppId } from '../../../utils/encryption';

const DisciplineSection = ({ data, onUpdate }) => {
    const navigate = useNavigate();
    const { orgId: urlOrgId } = useParams();
    const location = useLocation();

    // Function to get orgId from URL query parameters with decryption
    const getOrgIdFromQuery = useCallback(() => {
        const searchParams = new URLSearchParams(location.search);
        const encryptedOrgId = searchParams.get('orgId');
        if (encryptedOrgId) {
            try {
                const decryptedOrgId = decryptText(encryptedOrgId);
                console.log('📍 DisciplineSection - Decrypted orgId from URL query:', decryptedOrgId);
                console.log('📍 DisciplineSection - Original encrypted orgId:', encryptedOrgId);
                return decryptedOrgId;
            } catch (error) {
                console.log('⚠️ DisciplineSection - Failed to decrypt orgId, using original:', encryptedOrgId, error);
                return encryptedOrgId; // Return original if decryption fails
            }
        }
        return null;
    }, [location.search]);

    // Function to get appId from URL query parameters with decryption
    const getAppIdFromQuery = useCallback(() => {
        const searchParams = new URLSearchParams(location.search);
        const encryptedAppId = searchParams.get('appId');
        if (encryptedAppId) {
            try {
                const decryptedAppId = decryptAppId(encryptedAppId);
                console.log('📍 DisciplineSection - Decrypted appId from URL query:', decryptedAppId);
                console.log('📍 DisciplineSection - Original encrypted appId:', encryptedAppId);
                return decryptedAppId;
            } catch (error) {
                console.log('⚠️ DisciplineSection - Failed to decrypt appId, using original:', encryptedAppId, error);
                return encryptedAppId; // Return original if decryption fails
            }
        }
        return null;
    }, [location.search]);

    // Function to get orgId with priority: URL query (encrypted) > URL params > null
    const getOrgId = useCallback(() => {
        // First check URL query parameters (encrypted) - highest priority
        const queryOrgId = getOrgIdFromQuery();
        if (queryOrgId) {
            console.log('📍 DisciplineSection - Using decrypted orgId from URL query:', queryOrgId);
            return queryOrgId;
        }

        // Then check URL parameters (unencrypted)
        if (urlOrgId) {
            console.log('📍 DisciplineSection - Using orgId from URL params:', urlOrgId);
            return urlOrgId;
        }

        // No fallback - orgId must be provided via URL
        console.log('⚠️ DisciplineSection - No orgId found in URL parameters');
        return null;
    }, [getOrgIdFromQuery, urlOrgId]);

    // Function to get appId with priority: URL query (encrypted) only
    const getAppId = useCallback(() => {
        // Only check URL query parameters (encrypted)
        const queryAppId = getAppIdFromQuery();
        if (queryAppId) {
            console.log('📍 DisciplineSection - Using decrypted appId from URL query:', queryAppId);
            return queryAppId;
        }

        // No fallback - appId must be provided via URL query parameters
        console.log('⚠️ DisciplineSection - No appId found in URL query parameters');
        return null;
    }, [getAppIdFromQuery]);

    // Function to get assessorId from URL query parameters with decryption
    const getAssessorIdFromQuery = useCallback(() => {
        const searchParams = new URLSearchParams(location.search);
        const encryptedAssessorId = searchParams.get('assessorId');
        if (encryptedAssessorId) {
            try {
                const decryptedAssessorId = decryptText(encryptedAssessorId);
                console.log('📍 DisciplineSection - Decrypted assessorId from URL query:', decryptedAssessorId);
                return decryptedAssessorId;
            } catch (error) {
                console.log('⚠️ DisciplineSection - Failed to decrypt assessorId:', error);
                return null;
            }
        }
        return null;
    }, [location.search]);

    // Function to get assessmentId from URL query parameters with decryption
    const getAssessmentIdFromQuery = useCallback(() => {
        const searchParams = new URLSearchParams(location.search);
        const encryptedAssessmentId = searchParams.get('assessmentId');
        if (encryptedAssessmentId) {
            try {
                const decryptedAssessmentId = decryptText(encryptedAssessmentId);
                console.log('📍 DisciplineSection - Decrypted assessmentId from URL query:', decryptedAssessmentId);
                return decryptedAssessmentId;
            } catch (error) {
                console.log('⚠️ DisciplineSection - Failed to decrypt assessmentId:', error);
                return null;
            }
        }
        return null;
    }, [location.search]);

    // Function to get labTypeId from URL query parameters with decryption
    const getLabTypeIdFromQuery = useCallback(() => {
        const searchParams = new URLSearchParams(location.search);
        const encryptedLabTypeId = searchParams.get('labTypeId');
        if (encryptedLabTypeId) {
            try {
                const decryptedLabTypeId = decryptText(encryptedLabTypeId);
                console.log('📍 DisciplineSection - Decrypted labTypeId from URL query:', decryptedLabTypeId);
                return decryptedLabTypeId;
            } catch (error) {
                console.log('⚠️ DisciplineSection - Failed to decrypt labTypeId:', error);
                return null;
            }
        }
        return null;
    }, [location.search]);

    // Function to get capacity from URL query parameters with decryption (if needed)
    const getCapacityFromQuery = useCallback(() => {
        const searchParams = new URLSearchParams(location.search);
        const encryptedCapacity = searchParams.get('capacity');
        if (encryptedCapacity) {
            try {
                const decryptedCapacity = decryptText(encryptedCapacity);
                console.log('📍 DisciplineSection - Decrypted capacity from URL query:', decryptedCapacity);
                return decryptedCapacity;
            } catch (error) {
                console.log('⚠️ DisciplineSection - Failed to decrypt capacity:', error);
                return null;
            }
        }
        return null;
    }, [location.search]);

    const [orgId, setOrgId] = useState(() => {
        // Initialize with query parameters first, then URL params
        const searchParams = new URLSearchParams(location.search);
        const encryptedOrgId = searchParams.get('orgId');
        if (encryptedOrgId) {
            try {
                const decryptedOrgId = decryptText(encryptedOrgId);
                console.log('🚀 DisciplineSection - Initial orgId from encrypted query:', decryptedOrgId);
                return decryptedOrgId;
            } catch (error) {
                console.log('Failed to decrypt initial orgId, using original:', encryptedOrgId);
                return encryptedOrgId;
            }
        }
        // Only fallback to URL params, no localStorage
        return urlOrgId || null;
    });

    const [appId, setAppId] = useState(() => {
        // Initialize with query parameters only
        const searchParams = new URLSearchParams(location.search);
        const encryptedAppId = searchParams.get('appId');
        if (encryptedAppId) {
            try {
                const decryptedAppId = decryptAppId(encryptedAppId);
                console.log('🚀 DisciplineSection - Initial appId from encrypted query:', decryptedAppId);
                return decryptedAppId;
            } catch (error) {
                console.log('Failed to decrypt initial appId, using original:', encryptedAppId);
                return encryptedAppId;
            }
        }
        // No fallback - must be provided via query parameters
        return null;
    });

    // State for assessment parameters
    const [assessorId, setAssessorId] = useState(() => {
        const searchParams = new URLSearchParams(location.search);
        const encryptedAssessorId = searchParams.get('assessorId');
        if (encryptedAssessorId) {
            try {
                const decryptedAssessorId = decryptText(encryptedAssessorId);
                console.log('🚀 DisciplineSection - Initial assessorId from encrypted query:', decryptedAssessorId);
                return decryptedAssessorId;
            } catch (error) {
                console.log('Failed to decrypt initial assessorId:', error);
                return null;
            }
        }
        return null;
    });

    const [assessmentId, setAssessmentId] = useState(() => {
        const searchParams = new URLSearchParams(location.search);
        const encryptedAssessmentId = searchParams.get('assessmentId');
        if (encryptedAssessmentId) {
            try {
                const decryptedAssessmentId = decryptText(encryptedAssessmentId);
                console.log('🚀 DisciplineSection - Initial assessmentId from encrypted query:', decryptedAssessmentId);
                return decryptedAssessmentId;
            } catch (error) {
                console.log('Failed to decrypt initial assessmentId:', error);
                return null;
            }
        }
        return null;
    });

    const [labTypeId, setLabTypeId] = useState(() => {
        const searchParams = new URLSearchParams(location.search);
        const encryptedLabTypeId = searchParams.get('labTypeId');
        if (encryptedLabTypeId) {
            try {
                const decryptedLabTypeId = decryptText(encryptedLabTypeId);
                console.log('🚀 DisciplineSection - Initial labTypeId from encrypted query:', decryptedLabTypeId);
                return decryptedLabTypeId;
            } catch (error) {
                console.log('Failed to decrypt initial labTypeId:', error);
                return null;
            }
        }
        return null;
    });

    const [capacity, setCapacity] = useState(() => {
        const searchParams = new URLSearchParams(location.search);
        const encryptedCapacity = searchParams.get('capacity');
        if (encryptedCapacity) {
            try {
                const decryptedCapacity = decryptText(encryptedCapacity);
                console.log('🚀 DisciplineSection - Initial capacity from encrypted query:', decryptedCapacity);
                return decryptedCapacity;
            } catch (error) {
                console.log('Failed to decrypt initial capacity:', error);
                return null;
            }
        }
        return null;
    });

    // Update orgId state only (no localStorage)
    const updateOrgId = (newOrgId) => {
        console.log('Updating orgId state:', newOrgId);
        setOrgId(newOrgId);
    };

    // Update appId state only (no localStorage)
    const updateAppId = (newAppId) => {
        console.log('Updating appId state:', newAppId);
        setAppId(newAppId);
    };

    // Update assessment parameters
    const updateAssessorId = (newAssessorId) => {
        console.log('Updating assessorId state:', newAssessorId);
        setAssessorId(newAssessorId);
    };

    const updateAssessmentId = (newAssessmentId) => {
        console.log('Updating assessmentId state:', newAssessmentId);
        setAssessmentId(newAssessmentId);
    };

    const updateLabTypeId = (newLabTypeId) => {
        console.log('Updating labTypeId state:', newLabTypeId);
        setLabTypeId(newLabTypeId);
    };

    const updateCapacity = (newCapacity) => {
        console.log('Updating capacity state:', newCapacity);
        setCapacity(newCapacity);
    };


    const getAssessorDisciplines = async (assessorId, appId) => {
        try {
            const response = await TestingLaboratoryService.assessorDiscipline(assessorId, appId);
            if (response.success) {
                return response.data;
            }
        } catch (error) {
            console.error('Error fetching assessor disciplines:', error);
        }
        return [];
    };

    // useEffect to handle parameter updates from URL
    useEffect(() => {
        getAssessorDisciplines(assessorId, appId);
        const currentOrgId = getOrgId();
        if (currentOrgId !== orgId) {
            updateOrgId(currentOrgId);
        }

        const currentAppId = getAppId();
        if (currentAppId !== appId) {
            console.log('DisciplineSection - AppId from URL query params:', currentAppId);
            updateAppId(currentAppId);
        }

        // Handle assessment parameters
        const currentAssessorId = getAssessorIdFromQuery();
        if (currentAssessorId !== assessorId) {
            console.log('DisciplineSection - AssessorId from URL query params:', currentAssessorId);
            updateAssessorId(currentAssessorId);
        }

        const currentAssessmentId = getAssessmentIdFromQuery();
        if (currentAssessmentId !== assessmentId) {
            console.log('DisciplineSection - AssessmentId from URL query params:', currentAssessmentId);
            updateAssessmentId(currentAssessmentId);
        }

        const currentLabTypeId = getLabTypeIdFromQuery();
        if (currentLabTypeId !== labTypeId) {
            console.log('DisciplineSection - LabTypeId from URL query params:', currentLabTypeId);
            updateLabTypeId(currentLabTypeId);
        }

        const currentCapacity = getCapacityFromQuery();
        if (currentCapacity !== capacity) {
            console.log('DisciplineSection - Capacity from URL query params:', currentCapacity);
            updateCapacity(currentCapacity);
        }
    }, [
        urlOrgId,
        location.search,
        orgId,
        appId,
        assessorId,
        assessmentId,
        labTypeId,
        capacity,
        getOrgId,
        getAppId,
        getAssessorIdFromQuery,
        getAssessmentIdFromQuery,
        getLabTypeIdFromQuery,
        getCapacityFromQuery
    ]);

    // State management
    const [disciplines, setDisciplines] = useState([]);
    const [selectedDisciplines, setSelectedDisciplines] = useState([]);
    const [loading, setLoading] = useState(false);
    const [addingDiscipline, setAddingDiscipline] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [disciplineToDelete, setDisciplineToDelete] = useState(null);
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
    const [showMedicalImagingColumns, setShowMedicalImagingColumns] = useState(false);
    const [applicationStage, setApplicationStage] = useState(null);
    const [labCategory, setLabCategory] = useState(null);
    const [formData, setFormData] = useState({
        selectedDiscipline: '',
        selectedGroup: '',
        ...data
    });

    // Group state management
    const [groups, setGroups] = useState([]);
    const [groupLoading, setGroupLoading] = useState(false);

    // Instructions state
    const [instructions, setInstructions] = useState([]);
    const [instructionsLoading, setInstructionsLoading] = useState(false);

    // Check if current user is a sub-user
    const isSubUser = localStorage.getItem('isSubUser') === 'true';

    // Update parent component when form data changes
    useEffect(() => {
        if (onUpdate) {
            onUpdate(formData);
        }
    }, [formData, onUpdate]);

    // Fetch application stage
    const getApplicationStage = async (appId) => {
        try {
            if (!appId) return;

            console.log('🔍 DisciplineSection - getApplicationStage using appId:', appId);
            const response = await fetch(`${process.env.REACT_APP_API_URL}/testing-laboratory/application-stage/${appId}`);
            const result = await response.json();

            if (result.success) {
                setApplicationStage(result.stage);
            } else {
                console.log('Stage not found');
                setApplicationStage(null);
            }
        } catch (error) {
            console.error('Error fetching application stage:', error);
            setApplicationStage(null);
        }
    };



    // Load disciplines based on labTypeId and disciplineType from database
    const loadDisciplines = useCallback(async () => {
        setLoading(true);
        try {
            // Get labTypeId from localStorage (default to 3)
            const labTypeId = localStorage.getItem('labTypeId') || '3';

            // First try to get disciplineType from Form153LabDetails_new using appId
            let disciplineType = 'Diagnostic'; // default

            if (appId) {
                try {
                    // Try to get the laboratory details to find the actual disciplineType and category
                    console.log('🔍 DisciplineSection - getLaboratoryDetails using orgId:', orgId);
                    const result = await TestingLaboratoryService.getLaboratoryDetails(orgId);
                    if (result.success && result.data) {
                        if (result.data.CabType) {
                            // Use CabType from the database as disciplineType
                            disciplineType = result.data.CabType;
                        }
                        // Store the laboratory category for validation
                        if (result.data.AccreditationCategory) {
                            setLabCategory(result.data.AccreditationCategory);
                            console.log('🔍 Laboratory Category loaded:', result.data.AccreditationCategory);
                        }
                    }
                } catch (error) {
                    console.warn('Could not fetch disciplineType from database, using default');
                }
            }

            console.log('🔍 DisciplineSection - getDisciplines using params:', {
                labTypeId,
                disciplineType,
                orgId,
                appId
            });

            const result = await TestingLaboratoryService.getDisciplines(labTypeId, disciplineType, orgId, appId);
            if (result.success) {
                let disciplinesToDisplay = result.data;

                // Filter disciplines for subusers based on their assigned disciplines
                const isSubUser = localStorage.getItem('isSubUser') === 'true';
                if (isSubUser) {
                    try {
                        const disciplineIdsString = localStorage.getItem('disciplineIds');
                        if (disciplineIdsString) {
                            const assignedDisciplineIds = JSON.parse(disciplineIdsString);
                            console.log('🔍 Subuser assigned discipline IDs:', assignedDisciplineIds);

                            // Log first discipline to see its structure
                            if (result.data.length > 0) {
                                console.log('🔍 Sample discipline object:', result.data[0]);
                                console.log('🔍 Available keys:', Object.keys(result.data[0]));
                            }

                            if (Array.isArray(assignedDisciplineIds) && assignedDisciplineIds.length > 0) {
                                disciplinesToDisplay = result.data.filter(discipline => {
                                    // Get discipline ID - the property is lowercase 'id'
                                    const disciplineId = parseInt(discipline.id || discipline.Id || discipline.DisciplineId || discipline.disciplineId);

                                    console.log(`🔍 Checking discipline: ID=${disciplineId}, Name=${discipline.disciplineName || discipline.DisciplineName}, Assigned IDs:`, assignedDisciplineIds);

                                    return assignedDisciplineIds.includes(disciplineId);
                                });

                                console.log('🔍 Filtered disciplines for subuser:', disciplinesToDisplay);
                                console.log(`Showing ${disciplinesToDisplay.length} out of ${result.data.length} disciplines for subuser`);
                            }
                        }
                    } catch (error) {
                        console.error('Error parsing disciplineIds:', error);
                        // If parsing fails, show all disciplines
                    }
                }

                // Filter disciplines based on assessor disciplines if assessorId is present
                if (assessorId) {
                    try {
                        console.log('🔍 Filtering disciplines for assessorId:', assessorId);

                        const assessorDisciplinesResult = await getAssessorDisciplines(assessorId, appId);
                        console.log('🔍 Assessor disciplines from API:', assessorDisciplinesResult);
                        if (
                            assessorDisciplinesResult &&
                            Array.isArray(assessorDisciplinesResult) &&
                            assessorDisciplinesResult.length > 0
                        ) {
                            // Collect ALL discipline IDs from response
                            const assessorDisciplineIds = [
                                ...new Set(
                                    assessorDisciplinesResult
                                        .filter(item => item?.Disciplines)
                                        .flatMap(item =>
                                            item.Disciplines
                                                .split(',')
                                                .map(id => parseInt(id.trim()))
                                                .filter(Boolean)
                                        )
                                )
                            ];

                            console.log('✅ Assessor Discipline IDs:', assessorDisciplineIds);

                            if (assessorDisciplineIds.length > 0) {
                                const originalCount = disciplinesToDisplay.length;

                                disciplinesToDisplay = disciplinesToDisplay.filter(discipline => {
                                    const disciplineId = parseInt(
                                        discipline.id ||
                                        discipline.Id ||
                                        discipline.DisciplineId ||
                                        discipline.disciplineId
                                    );

                                    return assessorDisciplineIds.includes(disciplineId);
                                });

                                console.log(
                                    `📌 Disciplines filtered: ${originalCount} → ${disciplinesToDisplay.length}`
                                );
                            }
                        }
                    } catch (error) {
                        console.error('❌ Error filtering disciplines for assessor:', error);
                        // fallback: show all disciplines
                    }
                }
                setDisciplines(disciplinesToDisplay);
                showNotification(`Found ${disciplinesToDisplay.length} disciplines`, 'success');
                
                // Preload groups for the first discipline if available
                if (disciplinesToDisplay && disciplinesToDisplay.length > 0) {
                    const firstDisciplineId = disciplinesToDisplay[0].DisciplineId || disciplinesToDisplay[0].id;
                    console.log('📊 DisciplineSection - Preloading groups for first discipline ID:', firstDisciplineId);
                    
                    // Preload groups
                    try {
                        const groupResult = await axios.get(
                            `${process.env.REACT_APP_API_URL}/testing-laboratory/group-master`,
                            {
                                params: {
                                    labTypeId: labTypeId,
                                    disciplineId: firstDisciplineId
                                }
                            }
                        );

                        if (groupResult.data.success) {
                            setGroups(groupResult.data.data || []);
                            console.log(`📊 DisciplineSection - Groups preloaded for first discipline:`, groupResult.data.data);
                        } else {
                            console.warn('Failed to preload groups for first discipline');
                        }
                    } catch (error) {
                        console.warn('Error preloading groups for first discipline:', error);
                    }
                }
            } else {
                setDisciplines([]);
                showNotification('No disciplines found for the given criteria', 'warning');
            }
        } catch (error) {
            console.error('Error loading disciplines:', error);
            setDisciplines([]);
            showNotification('Error loading disciplines: ' + error.message, 'error');
        } finally {
            setLoading(false);
        }
    }, [orgId, appId, assessorId]);

    // Handle form field changes
    const handleChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Show notification
    const showNotification = (message, severity = 'success') => {
        setNotification({ open: true, message, severity });
    };

    // Close notification
    const closeNotification = () => {
        setNotification({ open: false, message: '', severity: 'success' });
    };

    // Load selected disciplines
    const loadSelectedDisciplines = useCallback(async () => {
        if (!orgId || !appId) return;

        try {
            // For subusers, use mainOrgId instead of their own orgId
            const isSubUser = localStorage.getItem('isSubUser') === 'true';
            const effectiveOrgId = isSubUser ? (localStorage.getItem('mainOrgId') || orgId) : orgId;

            console.log('🔍 DisciplineSection - getSelectedDisciplines using appId:', appId, 'orgId:', effectiveOrgId, 'isSubUser:', isSubUser);
            const result = await TestingLaboratoryService.getSelectedDisciplines(appId, effectiveOrgId);
            if (result.success) {
                console.log('🔍 RAW selected disciplines from API:', result.data);

                let selectedDisciplinesToDisplay = result.data;

                // Filter selected disciplines for subusers
                if (isSubUser) {
                    try {
                        const disciplineIdsString = localStorage.getItem('disciplineIds');
                        if (disciplineIdsString) {
                            const assignedDisciplineIds = JSON.parse(disciplineIdsString);
                            console.log('🔍 Filtering selected disciplines for subuser. Assigned IDs:', assignedDisciplineIds);

                            // Log first selected discipline to see its structure
                            if (result.data.length > 0) {
                                console.log('🔍 Sample selected discipline object:', result.data[0]);
                                console.log('🔍 Selected discipline keys:', Object.keys(result.data[0]));
                            }

                            if (Array.isArray(assignedDisciplineIds) && assignedDisciplineIds.length > 0) {
                                selectedDisciplinesToDisplay = result.data.filter(discipline => {
                                    // The selected disciplines might have different property names
                                    const disciplineId = parseInt(discipline.DisciplineId || discipline.disciplineId || discipline.Id || discipline.id);

                                    console.log(`🔍 Checking selected discipline: ID=${disciplineId}, Name=${discipline.DisciplineName || discipline.disciplineName}`);

                                    return assignedDisciplineIds.includes(disciplineId);
                                });

                                console.log(`🔍 Filtered selected disciplines: ${selectedDisciplinesToDisplay.length} out of ${result.data.length}`);
                            }
                        }
                    } catch (error) {
                        console.error('Error filtering selected disciplines for subuser:', error);
                    }
                }

                // Filter selected disciplines based on assessor disciplines if assessorId is present
                if (assessorId) {
                    try {
                        console.log('🔍 Filtering selected disciplines for assessorId:', assessorId);
                        const assessorDisciplinesResult = await getAssessorDisciplines(assessorId, appId);

                        if (assessorDisciplinesResult && assessorDisciplinesResult.length > 0) {
                            // Extract discipline IDs from the API response
                            let assessorDisciplineIds = [];

                            // Handle the response format: {"Disciplines":"393,45,102,267"} - supports multiple disciplines
                            if (assessorDisciplinesResult[0] && assessorDisciplinesResult[0].Disciplines) {
                                const disciplineIdsString = assessorDisciplinesResult[0].Disciplines;
                                assessorDisciplineIds = disciplineIdsString.split(',').map(id => parseInt(id.trim()));
                                console.log('🔍 Raw assessor disciplines for selected filter:', disciplineIdsString);
                                console.log('🔍 Parsed assessor discipline IDs for selected filter:', assessorDisciplineIds);
                            }

                            if (assessorDisciplineIds.length > 0) {
                                console.log(`🔍 Total existing selected disciplines before filtering: ${selectedDisciplinesToDisplay.length}`);

                                // Filter selected disciplines to show only those assigned to the assessor
                                const originalSelectedCount = selectedDisciplinesToDisplay.length;
                                selectedDisciplinesToDisplay = selectedDisciplinesToDisplay.filter(discipline => {
                                    const disciplineId = parseInt(discipline.DisciplineId || discipline.disciplineId || discipline.Id || discipline.id);
                                    const isIncluded = assessorDisciplineIds.includes(disciplineId);

                                    console.log(`🔍 Checking existing selected discipline: ID=${disciplineId}, Name=${discipline.DisciplineName || discipline.disciplineName}, Included=${isIncluded}`);

                                    return isIncluded;
                                });

                                console.log('🔍 Filtered existing selected disciplines for assessor:', selectedDisciplinesToDisplay);
                                console.log(`🔍 Selected disciplines filtering result: ${selectedDisciplinesToDisplay.length}/${originalSelectedCount} disciplines shown`);
                                console.log(`🔍 Assessor can view selected disciplines with IDs: [${selectedDisciplinesToDisplay.map(d => d.DisciplineId || d.disciplineId || d.Id || d.id).join(', ')}]`);
                            }
                        }
                    } catch (error) {
                        console.error('Error filtering selected disciplines for assessor:', error);
                        // Continue with all selected disciplines if assessor filtering fails
                    }
                }

                setSelectedDisciplines(selectedDisciplinesToDisplay);
                console.log('Final loaded selected disciplines:', selectedDisciplinesToDisplay);

                // Summary log for assessor discipline filtering
                if (assessorId && selectedDisciplinesToDisplay.length > 0) {
                    console.log(`📊 ASSESSOR DISCIPLINE SUMMARY for AssessorId: ${assessorId}`);
                    console.log(`📊 Total selected disciplines visible: ${selectedDisciplinesToDisplay.length}`);
                    console.log(`📊 Selected discipline IDs shown: [${selectedDisciplinesToDisplay.map(d => d.DisciplineId || d.disciplineId || d.Id || d.id).join(', ')}]`);
                    console.log(`📊 Selected discipline names: [${selectedDisciplinesToDisplay.map(d => d.DisciplineName || d.disciplineName).join(', ')}]`);
                }

                // Check if any selected discipline has cabtype as 'medical imaging' or 'both'
                const hasMedicalImaging = selectedDisciplinesToDisplay.some(discipline =>
                    discipline.cabtype &&
                    (discipline.cabtype.toLowerCase().includes('medical imaging') ||
                        discipline.cabtype.toLowerCase() === 'both')
                );
                setShowMedicalImagingColumns(hasMedicalImaging);
            } else {
                setSelectedDisciplines([]);
                setShowMedicalImagingColumns(false);
            }
        } catch (error) {
            console.error('Error loading selected disciplines:', error);
            setSelectedDisciplines([]);
            setShowMedicalImagingColumns(false);
            showNotification('Error loading selected disciplines: ' + error.message, 'error');
        }
    }, [orgId, appId, assessorId]);

    // Fetch instructions from API
    useEffect(() => {
        const fetchInstructions = async () => {
            try {
                setInstructionsLoading(true);
                const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/reactApi/api';
                const response = await fetch(`${API_BASE_URL}/instructions?sectionName=Discipline&labTypeId=1`);
                const data = await response.json();

                if (data.success && data.data.instructions) {
                    // Sort instructions by orderNo
                    const sortedInstructions = data.data.instructions.sort((a, b) => a.orderNo - b.orderNo);
                    setInstructions(sortedInstructions);
                }
            } catch (error) {
                console.error('Error fetching instructions:', error);
                // Fallback to default instructions if API fails
                setInstructions([]);
            } finally {
                setInstructionsLoading(false);
            }
        };

        fetchInstructions();
    }, []);

    // Load disciplines when component mounts or dependencies change
    useEffect(() => {
        if (orgId) {
            loadDisciplines();
            loadSelectedDisciplines();
        }
        if (appId) {
            getApplicationStage(appId);
        }
    }, [orgId, appId, loadDisciplines, loadSelectedDisciplines]);

    // Check if discipline addition is blocked due to category limit
    const isDisciplineAddBlocked = () => {
        if (selectedDisciplines.length >= 2 && labCategory) {
            const categoryLower = labCategory.toLowerCase();
            return categoryLower.includes('micro') || categoryLower.includes('mini');
        }
        return false;
    };

    // Load groups based on selected discipline and labTypeId
    const loadGroups = useCallback(async (disciplineId) => {
        if (!disciplineId) {
            setGroups([]);
            return;
        }

        // Get labTypeId from state, or fallback to localStorage
        const effectiveLabTypeId = labTypeId || localStorage.getItem('labTypeId') || '3';
        console.log(`📊 loadGroups - disciplineId: ${disciplineId}, effectiveLabTypeId: ${effectiveLabTypeId}`);

        if (!effectiveLabTypeId) {
            console.warn('⚠️ loadGroups - No labTypeId available');
            setGroups([]);
            return;
        }

        setGroupLoading(true);
        try {
            const result = await axios.get(
                `${process.env.REACT_APP_API_URL}/testing-laboratory/group-master`,
                {
                    params: {
                        labTypeId: effectiveLabTypeId,
                        disciplineId: disciplineId
                    }
                }
            );

            if (result.data.success) {
                const groupsData = result.data.data || [];
                console.log(`✅ Groups loaded successfully:`, groupsData);
                console.log(`📊 Group field names:`, groupsData.length > 0 ? Object.keys(groupsData[0]) : 'No groups');
                setGroups(groupsData);
                console.log(`📊 DisciplineSection - Groups loaded for disciplineId: ${disciplineId}`, groupsData);
                // Reset selected group when discipline changes
                setFormData(prev => ({ ...prev, selectedGroup: '' }));
            } else {
                setGroups([]);
                console.warn('Failed to load groups:', result.data.message);
            }
        } catch (error) {
            console.error('Error loading groups:', error);
            setGroups([]);
            showNotification('Error loading groups: ' + error.message, 'error');
        } finally {
            setGroupLoading(false);
        }
    }, [labTypeId]);

    // Effect to load groups when discipline is selected
    useEffect(() => {
        if (formData.selectedDiscipline) {
            loadGroups(formData.selectedDiscipline);
        } else {
            setGroups([]);
        }
    }, [formData.selectedDiscipline, loadGroups]);

    // Add discipline selection
    const handleAddDiscipline = async () => {
        if (!formData.selectedDiscipline) {
            showNotification('Please select a discipline first', 'warning');
            return;
        }
        if (!formData.selectedGroup) {
            showNotification('Please select a group first', 'warning');
            return;
        }

        // Check if trying to add third discipline when category is Micro or Mini
        if (selectedDisciplines.length >= 2) {
            // Use cached category or fetch if not available
            let category = labCategory;

            if (!category) {
                try {
                    // Fetch laboratory details to get AccreditationCategory
                    console.log('🔍 DisciplineSection - Checking category before adding third discipline');
                    const labDetailsResult = await TestingLaboratoryService.getLaboratoryDetails(orgId);

                    if (labDetailsResult.success && labDetailsResult.data) {
                        category = labDetailsResult.data.AccreditationCategory || '';
                        setLabCategory(category);
                    }
                } catch (error) {
                    console.error('Error fetching laboratory category:', error);
                }
            }

            if (category) {
                console.log('🔍 Laboratory Category:', category);

                // Check if category is Micro or Mini (case-insensitive)
                const categoryLower = category.toLowerCase();
                if (categoryLower.includes('micro') || categoryLower.includes('mini')) {
                    showNotification(
                        `Cannot add more than 2 disciplines. Your laboratory category is "${category}". Please change the category from Laboratory Section from Micro or Mini to Small Laboratories to add more disciplines.`,
                        'error'
                    );
                    return; // STOP - Do not proceed with adding discipline
                }
            }
        }

        setAddingDiscipline(true);
        try {
            const insertedBy = localStorage.getItem('orgId') || '0';
            const result = await TestingLaboratoryService.addDisciplineSelection(appId, orgId, formData.selectedDiscipline, formData.selectedGroup, insertedBy);
            if (result.success) {
                showNotification('Discipline added successfully', 'success');
                // Reset the form and reload data
                setFormData(prev => ({ ...prev, selectedDiscipline: '', selectedGroup: '' }));
                loadSelectedDisciplines();
            } else {
                showNotification(result.message || 'Failed to add discipline', 'error');
            }
        } catch (error) {
            console.error('Error adding discipline:', error);
            showNotification(error.message || 'Error adding discipline', 'error');
        } finally {
            setAddingDiscipline(false);
        }
    };

    // Handle delete discipline
    const handleDeleteDiscipline = (discipline) => {
        setDisciplineToDelete(discipline);
        setDeleteDialogOpen(true);
    };

    // Confirm delete discipline
    const confirmDeleteDiscipline = async () => {
        if (!disciplineToDelete) return;

        try {
            const result = await TestingLaboratoryService.deleteDisciplineSelection(disciplineToDelete.Id);
            if (result.success) {
                showNotification('Discipline removed successfully', 'success');
                loadSelectedDisciplines();
            } else {
                showNotification(result.message || 'Failed to remove discipline', 'error');
            }
        } catch (error) {
            console.error('Error deleting discipline:', error);
            showNotification(error.message || 'Error removing discipline', 'error');
        } finally {
            setDeleteDialogOpen(false);
            setDisciplineToDelete(null);
        }
    };

    // Cancel delete
    const cancelDelete = () => {
        setDeleteDialogOpen(false);
        setDisciplineToDelete(null);
    };

    // Helper function to build all URL parameters
    const buildUrlParameters = () => {
        const currentOrgId = getOrgId();
        const currentAppId = getAppId();
        const currentAssessorId = getAssessorIdFromQuery();
        const currentAssessmentId = getAssessmentIdFromQuery();
        const currentLabTypeId = getLabTypeIdFromQuery();
        const currentCapacity = getCapacityFromQuery();

        let params = '';
        if (currentOrgId) {
            params += `&orgId=${encodeURIComponent(encryptText(currentOrgId))}`;
        }
        if (currentAppId) {
            params += `&appId=${encodeURIComponent(encryptAppId(currentAppId))}`;
        }
        if (currentAssessorId) {
            params += `&assessorId=${encodeURIComponent(encryptText(currentAssessorId))}`;
        }
        if (currentAssessmentId) {
            params += `&assessmentId=${encodeURIComponent(encryptText(currentAssessmentId))}`;
        }
        if (currentLabTypeId) {
            params += `&labTypeId=${encodeURIComponent(encryptText(currentLabTypeId))}`;
        }
        if (currentCapacity) {
            params += `&capacity=${encodeURIComponent(encryptText(currentCapacity))}`;
        }
        return params;
    };

    // Equipment handlers
    const handleEquipmentAdd = (discipline) => {
        const queryString = createEncryptedQueryString(discipline.DisciplineId, discipline.DisciplineName, discipline.GroupId,discipline.groupName, 0);
        const urlParams = buildUrlParameters();
        navigate(`/main/testing/equipment?${queryString}${urlParams}`);
    };

    const handleEquipmentBulk = (discipline) => {
        const queryString = createEncryptedQueryString(discipline.DisciplineId, discipline.DisciplineName, discipline.GroupId,discipline.groupName, 2);
        const urlParams = buildUrlParameters();
        navigate(`/main/testing/equipment?${queryString}${urlParams}`);
    };

    const handleEquipmentView = (discipline) => {
        console.log('Equipment View clicked for discipline:', discipline);
        // Navigate to equipment section with tab=2 (Existing Equipments) using encrypted parameters
        const queryString = createEncryptedQueryString(discipline.DisciplineId, discipline.DisciplineName,discipline.GroupId,discipline.groupName, 3);
        const urlParams = buildUrlParameters();
        navigate(`/main/testing/equipment?${queryString}${urlParams}`);
    };

    // Reference Material handlers
    const handleReferenceAdd = (discipline) => {
        console.log('Reference Material Add clicked for discipline:', discipline);
        // Navigate to reference material section with tab=0 (Add Reference) using encrypted parameters
        const queryString = createEncryptedQueryString(discipline.DisciplineId, discipline.DisciplineName, discipline.GroupId, discipline.groupName,0);
        const urlParams = buildUrlParameters();
        navigate(`/main/testing/reference-material?${queryString}${urlParams}`);
    };

    const handleReferenceView = (discipline) => {
        console.log('Reference Material View clicked for discipline:', discipline);
        // Navigate to reference material section with tab=1 (View Existing) using encrypted parameters
        const queryString = createEncryptedQueryString(discipline.DisciplineId, discipline.DisciplineName, discipline.GroupId, discipline.groupName, 1);
        const urlParams = buildUrlParameters();
        navigate(`/main/testing/reference-material?${queryString}${urlParams}`);
    };

    // Scope handlers
    const handleScopeAdd = (discipline) => {
        console.log('Scope Add clicked for discipline:', discipline);

        // Navigate to scope page with tab=1 (Add Scope tab) using encrypted parameters
        const queryString = createEncryptedQueryString(discipline.DisciplineId, discipline.DisciplineName, discipline.GroupId, discipline.groupName, 0);
        const urlParams = buildUrlParameters();
        navigate(`/main/testing/scope?${queryString}${urlParams}`);
    };

    const handleScopeBulk = (discipline) => {
        console.log('Scope Bulk Update clicked for discipline:', discipline);

        // Navigate to scope page with tab=2 (Bulk Update) using encrypted parameters
        const queryString = createEncryptedQueryString(discipline.DisciplineId, discipline.DisciplineName, discipline.GroupId, discipline.groupName, 2);
        const urlParams = buildUrlParameters();
        navigate(`/main/testing/scope?${queryString}${urlParams}`);
    };

    const handleScopeView = (discipline) => {
        console.log('Scope View clicked for discipline:', discipline);

        // Navigate to scope page with tab=3 (Existing Scopes) using encrypted parameters
        const queryString = createEncryptedQueryString(discipline.DisciplineId, discipline.DisciplineName, discipline.GroupId, discipline.groupName, 3);
        const urlParams = buildUrlParameters();
        navigate(`/main/testing/scope?${queryString}${urlParams}`);
    };

    // Medical Imaging specific handlers
    const handlePhantomAdd = (discipline) => {
        // Navigate to phantoms page with tab=0 using encrypted parameters
        const queryString = createEncryptedQueryString(discipline.DisciplineId, discipline.DisciplineName, discipline.GroupId, discipline.groupName, 0);
        const urlParams = buildUrlParameters();
        navigate(`/main/testing/phantoms?${queryString}${urlParams}`);
    };

    const handlePhantomView = (discipline) => {
        // Navigate to phantoms page with tab=1 using encrypted parameters
        const queryString = createEncryptedQueryString(discipline.DisciplineId, discipline.DisciplineName, discipline.GroupId, discipline.groupName, 1);
        const urlParams = buildUrlParameters();
        navigate(`/main/testing/phantoms?${queryString}${urlParams}`);
    };

    const handleRadioisotopeAdd = (discipline) => {
        console.log('Radioisotope Add clicked for discipline:', discipline);

        // Navigate to radioisotopes page with tab=0 (Add) using encrypted parameters
        const queryString = createEncryptedQueryString(discipline.DisciplineId, discipline.DisciplineName, discipline.GroupId, discipline.groupName, 0);
        const urlParams = buildUrlParameters();
        navigate(`/main/testing/radioisotopes?${queryString}${urlParams}`);
    };

    const handleRadioisotopeView = (discipline) => {
        console.log('Radioisotope View clicked for discipline:', discipline);

        // Navigate to radioisotopes page with tab=1 (View) using encrypted parameters
        const queryString = createEncryptedQueryString(discipline.DisciplineId, discipline.DisciplineName, discipline.GroupId, discipline.groupName, 1);
        const urlParams = buildUrlParameters();
        navigate(`/main/testing/radioisotopes?${queryString}${urlParams}`);
    };

    return (
      <Box
        sx={{
          p: 3,
          backgroundColor: "#f8fafc",
          minHeight: "100vh",
        }}
      >
        {/* Instructions Section */}
        <Paper
          sx={{
            mb: 3,
            p: 3,
            backgroundColor: "#e3f2fd",
            border: "1px solid #1976d2",
            borderRadius: 2,
            boxShadow: "0 2px 8px rgba(25, 118, 210, 0.15)",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: "#1976d2",
              fontWeight: 600,
              mb: 2,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            📋 Instructions
          </Typography>

          {instructionsLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 2,
              }}
            >
              <CircularProgress size={24} sx={{ mr: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Loading instructions...
              </Typography>
            </Box>
          ) : instructions.length > 0 ? (
            <Box
              sx={{
                pl: 2,
                "& p": {
                  mb: 1,
                  fontSize: "0.95rem",
                  lineHeight: 1.6,
                  color: "#424242",
                },
              }}
            >
              {instructions.map((instruction, index) => (
                <Typography key={instruction.id} component="p">
                  {index + 1}. {instruction.instruction}
                </Typography>
              ))}
            </Box>
          ) : (
            <Box
              sx={{
                pl: 2,
                "& p": {
                  mb: 1,
                  fontSize: "0.95rem",
                  lineHeight: 1.6,
                  color: "#424242",
                },
              }}
            >
              <Typography component="p">
                i. Model first: Always try Model Equipment & Scope before using
                Other.
              </Typography>
              <Typography component="p">
                ii. Bulk = faster: Prefer Bulk Update when updating many records
                — download the pre-filled Excel, complete only what's missing,
                and upload.
              </Typography>
              <Typography component="p">
                iii. Edit safely: Use View Existing for corrections; avoid
                duplicate adds.
              </Typography>
              <Typography component="p">
                iv. Need help or missing values? Email supportwp@nabl.qcin.org.
              </Typography>
              <Typography component="p">
                v. Work in this sequence: Discipline → Equipment → Reference
                Materials → Scope.
              </Typography>
              <Typography component="p">
                vi. Always try to pick from the Model Equipment & Scope lists
                first.
              </Typography>
              <Typography component="p">
                vii. If a required value is missing from the Master List, email
                supportwp@nabl.qcin.org.
              </Typography>
            </Box>
          )}
        </Paper>

        {/* Main Content */}
        <Paper
          sx={{
            mb: 3,
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            borderRadius: 2,
          }}
        >
          <Box
            sx={{
              py: 2,
              px: 2,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              borderRadius: 1,
              mb: 3,
              "& .MuiAccordionSummary-content": {
                margin: "16px 0",
              },
              "& .MuiSvgIcon-root": {
                color: "white",
              },
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              🏢 Discipline/Group Selection
            </Typography>
          </Box>

          {/* Hide Add Discipline section for sub-users */}
          {!isSubUser &&
            (applicationStage === null || applicationStage === "18a") && (
              <Grid container spacing={3} py={2} px={3}>
                {/* Discipline Dropdown */}
                <Grid item xs={12} md={6}>
                  <FormControl
                    fullWidth
                    required
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "white",
                        borderRadius: 2,
                        "&:hover": {
                          boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                        },
                        "&.Mui-focused": {
                          boxShadow: "0 4px 12px rgba(25, 118, 210, 0.2)",
                        },
                      },
                    }}
                  >
                    <InputLabel>Select Discipline</InputLabel>
                    <Select
                      value={formData.selectedDiscipline || ""}
                      onChange={(e) =>
                        handleChange("selectedDiscipline", e.target.value)
                      }
                      label="Select Discipline"
                      disabled={
                        loading ||
                        disciplines.length === 0 ||
                        isDisciplineAddBlocked()
                      }
                    >
                      <MenuItem value="">
                        <em>Select Discipline</em>
                      </MenuItem>
                      {disciplines.map((discipline) => (
                        <MenuItem
                          key={discipline.DisciplineId || discipline.id}
                          value={discipline.DisciplineId || discipline.id}
                        >
                          {discipline.disciplineName || discipline.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Group Dropdown (appears when discipline is selected) */}
                {formData.selectedDiscipline && (
                  <Grid item xs={12} md={6}>
                    <FormControl
                      fullWidth
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "white",
                          borderRadius: 2,
                          "&:hover": {
                            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                          },
                          "&.Mui-focused": {
                            boxShadow: "0 4px 12px rgba(25, 118, 210, 0.2)",
                          },
                        },
                      }}
                    >
                      <InputLabel>Select Group</InputLabel>
                      <Select
                        value={formData.selectedGroup ? String(formData.selectedGroup) : ""}
                        onChange={(e) =>
                          handleChange("selectedGroup", e.target.value)
                        }
                        label="Select Group"
                        disabled={groupLoading || groups.length === 0}
                      >
                        <MenuItem value="">
                          <em>Select Group</em>
                        </MenuItem>
                        {groups && groups.length > 0 && groups.map((group) => (
                          <MenuItem
                            key={String(group.grpId || group.groupId)}
                            value={String(group.grpId || group.groupId)}
                          >
                            {group.groupName || group.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {groupLoading && (
                      <Box
                        sx={{ display: "flex", alignItems: "center", mt: 1 }}
                      >
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        <Typography variant="body2">
                          Loading groups...
                        </Typography>
                      </Box>
                    )}
                    {!groupLoading && groups.length === 0 && (
                      <Typography
                        variant="body2"
                        sx={{ color: "orange", mt: 1 }}
                      >
                        No groups found for the selected discipline
                      </Typography>
                    )}
                  </Grid>
                )}

                {/* Add Discipline Button */}
                <Grid item xs={12} md={formData.selectedDiscipline ? 12 : 6}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAddDiscipline}
                    disabled={
                      !formData.selectedDiscipline ||
                      addingDiscipline ||
                      isDisciplineAddBlocked()
                    }
                    startIcon={
                      addingDiscipline ? (
                        <CircularProgress size={20} />
                      ) : (
                        <AddIcon />
                      )
                    }
                    sx={{
                      height: "56px",
                      borderRadius: 2,
                      fontSize: "1rem",
                      fontWeight: 600,
                    }}
                    fullWidth
                  >
                    {addingDiscipline ? "Adding..." : "Add Discipline"}
                  </Button>
                </Grid>

                {/* Warning message when discipline limit is reached */}
                {isDisciplineAddBlocked() && (
                  <Grid item xs={12}>
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      <Typography variant="body1">
                        <strong>Discipline Limit Reached:</strong> Your
                        laboratory category is "{labCategory}". You can only add
                        up to 2 disciplines for Micro or Mini categories. Please
                        change the category from Laboratory Section to Small
                        Laboratories to add more disciplines.
                      </Typography>
                    </Alert>
                  </Grid>
                )}

                {/* Selected Discipline Display */}
                {formData.selectedDiscipline && (
                  <Grid item xs={12}>
                    <Alert severity="info" sx={{ mt: 2 }}>
                      <Typography variant="body1">
                        <strong>Selected Discipline:</strong>{" "}
                        {disciplines.find(
                          (d) =>
                            (d.DisciplineId || d.id) ===
                            formData.selectedDiscipline
                        )?.disciplineName ||
                          disciplines.find(
                            (d) =>
                              (d.DisciplineId || d.id) ===
                              formData.selectedDiscipline
                          )?.name ||
                          "Unknown"}
                      </Typography>
                    </Alert>
                  </Grid>
                )}

                {/* No Disciplines Message */}
                {!loading && disciplines.length === 0 && (
                  <Grid item xs={12}>
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      <Typography variant="body1">
                        No disciplines found. Please ensure:
                      </Typography>
                      <ul>
                        <li>OrgId and AppId are properly set</li>
                        <li>Laboratory details exist in the database</li>
                        <li>
                          Disciplines are configured for this laboratory type
                        </li>
                      </ul>
                    </Alert>
                  </Grid>
                )}
              </Grid>
            )}
        </Paper>

        {/* Selected Disciplines Table */}
        {selectedDisciplines.length > 0 && (
          <Paper
            sx={{
              p: 0,
              mb: 3,
              boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
              borderRadius: 3,
              border: "1px solid rgba(0,0,0,0.06)",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                py: 3,
                px: 4,
                background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                borderBottom: "1px solid rgba(0,0,0,0.06)",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: "#334155",
                  fontSize: "1.125rem",
                }}
              >
                📋 Selected Disciplines/Group ({selectedDisciplines.length})
              </Typography>
            </Box>

            <TableContainer
              sx={{
                "& .MuiTable-root": {
                  borderCollapse: "separate",
                  borderSpacing: 0,
                },
              }}
            >
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: "#fafbfc",
                      "& .MuiTableCell-head": {
                        borderBottom: "2px solid #e2e8f0",
                        fontSize: "0.875rem",
                        fontWeight: 600,
                        color: "#475569",
                        py: 2,
                        textTransform: "uppercase",
                        letterSpacing: "0.025em",
                      },
                    }}
                  >
                    <TableCell sx={{ minWidth: "200px" }}>
                      Discipline/Group Name{" "}
                    </TableCell>
                    <TableCell align="center" sx={{ minWidth: "140px" }}>
                      Equipment (4.1)
                    </TableCell>
                    <TableCell align="center" sx={{ minWidth: "160px" }}>
                      Reference Materials (4.2)
                    </TableCell>
                    <TableCell align="center" sx={{ minWidth: "140px" }}>
                      Scope (4.3)
                    </TableCell>
                    {showMedicalImagingColumns && (
                      <>
                        <TableCell align="center" sx={{ minWidth: "160px" }}>
                          List of Phantoms (4.4)
                        </TableCell>
                        <TableCell align="center" sx={{ minWidth: "180px" }}>
                          Radioisotopes/Test Kits (4.5)
                        </TableCell>
                      </>
                    )}
                    {!isSubUser &&
                      (applicationStage === null ||
                        applicationStage === "" ||
                        applicationStage === "18a") && (
                        <TableCell align="center" sx={{ width: "100px" }}>
                          Actions
                        </TableCell>
                      )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedDisciplines.map((discipline) => (
                    <TableRow
                      key={discipline.Id}
                      sx={{
                        "&:hover": {
                          backgroundColor: "#f8fafc",
                          transform: "translateY(-1px)",
                          boxShadow: "0 4px 8px rgba(0,0,0,0.04)",
                        },
                        "& .MuiTableCell-root": {
                          borderBottom: "1px solid #e2e8f0",
                          py: 2.5,
                          px: 2,
                        },
                        transition: "all 0.2s ease-in-out",
                        cursor: "default",
                      }}
                    >
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 500, color: "#334155" }}
                        >
                          {discipline?.DisciplineName}
                          {" -  "}
                          <span style={{ color: "#64748b" }}>
                            {discipline?.groupName}
                          </span>
                        </Typography>
                        <Chip
                          label={discipline.LabTypeName || "N/A"}
                          size="small"
                          sx={{
                            mt: 1,
                            backgroundColor: "#e0e7ff",
                            color: "#3730a3",
                            fontWeight: 500,
                            border: "none",
                            "& .MuiChip-label": {
                              px: 1.5,
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            gap: 1,
                          }}
                        >
                          {(applicationStage === null || applicationStage === "" || applicationStage === "18a") && (
                            <>
                          <IconButton
                            size="small"
                            onClick={() => handleEquipmentAdd(discipline)}
                            title="Add Equipment"
                            sx={{
                              width: 32,
                              height: 32,
                              backgroundColor: "#e0f2fe",
                              color: "#0277bd",
                              "&:hover": {
                                backgroundColor: "#b3e5fc",
                                transform: "scale(1.05)",
                              },
                              transition: "all 0.2s ease",
                            }}
                          >
                            <AddCircleIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleEquipmentBulk(discipline)}
                            title="Bulk Update Equipment"
                            sx={{
                              width: 32,
                              height: 32,
                              backgroundColor: "#f3e5f5",
                              color: "#7b1fa2",
                              "&:hover": {
                                backgroundColor: "#e1bee7",
                                transform: "scale(1.05)",
                              },
                              transition: "all 0.2s ease",
                            }}
                          >
                            <BulkUploadIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                          </>
                          )}
                          <IconButton
                            size="small"
                            onClick={() => handleEquipmentView(discipline)}
                            title="View Equipment"
                            sx={{
                              width: 32,
                              height: 32,
                              backgroundColor: "#e8f5e8",
                              color: "#2e7d32",
                              "&:hover": {
                                backgroundColor: "#c8e6c9",
                                transform: "scale(1.05)",
                              },
                              transition: "all 0.2s ease",
                            }}
                          >
                            <ViewIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            gap: 1,
                          }}
                        >
                          {(applicationStage === null || applicationStage === "" || applicationStage === "18a") && (
                          <IconButton
                            size="small"
                            onClick={() => handleReferenceAdd(discipline)}
                            title="Add Reference Material"
                            sx={{
                              width: 32,
                              height: 32,
                              backgroundColor: "#e0f2fe",
                              color: "#0277bd",
                              "&:hover": {
                                backgroundColor: "#b3e5fc",
                                transform: "scale(1.05)",
                              },
                              transition: "all 0.2s ease",
                            }}
                          >
                            <AddCircleIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                          )}
                          <IconButton
                            size="small"
                            onClick={() => handleReferenceView(discipline)}
                            title="View Reference Materials"
                            sx={{
                              width: 32,
                              height: 32,
                              backgroundColor: "#e8f5e8",
                              color: "#2e7d32",
                              "&:hover": {
                                backgroundColor: "#c8e6c9",
                                transform: "scale(1.05)",
                              },
                              transition: "all 0.2s ease",
                            }}
                          >
                            <ViewIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            gap: 1,
                          }}
                        >
                          {(applicationStage === null || applicationStage === "" || applicationStage === "18a") && (
                            <>
                          <IconButton
                            size="small"
                            onClick={() => handleScopeAdd(discipline)}
                            title="Add Scope"
                            sx={{
                              width: 32,
                              height: 32,
                              backgroundColor: "#e0f2fe",
                              color: "#0277bd",
                              "&:hover": {
                                backgroundColor: "#b3e5fc",
                                transform: "scale(1.05)",
                              },
                              transition: "all 0.2s ease",
                            }}
                          >
                            <AddCircleIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleScopeBulk(discipline)}
                            title="Bulk Update Scope"
                            sx={{
                              width: 32,
                              height: 32,
                              backgroundColor: "#f3e5f5",
                              color: "#7b1fa2",
                              "&:hover": {
                                backgroundColor: "#e1bee7",
                                transform: "scale(1.05)",
                              },
                              transition: "all 0.2s ease",
                            }}
                          >
                            <BulkUploadIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                          </>
                          )}
                          <IconButton
                            size="small"
                            onClick={() => handleScopeView(discipline)}
                            title="View Scope"
                            sx={{
                              width: 32,
                              height: 32,
                              backgroundColor: "#e8f5e8",
                              color: "#2e7d32",
                              "&:hover": {
                                backgroundColor: "#c8e6c9",
                                transform: "scale(1.05)",
                              },
                              transition: "all 0.2s ease",
                            }}
                          >
                            <ViewIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Box>
                      </TableCell>
                      {showMedicalImagingColumns && (
                        <>
                          <TableCell align="center">
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "center",
                                gap: 1,
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={() => handlePhantomAdd(discipline)}
                                title="Add Phantom"
                                sx={{
                                  width: 32,
                                  height: 32,
                                  backgroundColor: "#fff3e0",
                                  color: "#ef6c00",
                                  "&:hover": {
                                    backgroundColor: "#ffe0b2",
                                    transform: "scale(1.05)",
                                  },
                                  transition: "all 0.2s ease",
                                }}
                              >
                                <AddCircleIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handlePhantomView(discipline)}
                                title="View Phantoms"
                                sx={{
                                  width: 32,
                                  height: 32,
                                  backgroundColor: "#e8f5e8",
                                  color: "#2e7d32",
                                  "&:hover": {
                                    backgroundColor: "#c8e6c9",
                                    transform: "scale(1.05)",
                                  },
                                  transition: "all 0.2s ease",
                                }}
                              >
                                <ViewIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "center",
                                gap: 1,
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleRadioisotopeAdd(discipline)
                                }
                                title="Add Radioisotope/Test Kit"
                                sx={{
                                  width: 32,
                                  height: 32,
                                  backgroundColor: "#fce4ec",
                                  color: "#c2185b",
                                  "&:hover": {
                                    backgroundColor: "#f8bbd9",
                                    transform: "scale(1.05)",
                                  },
                                  transition: "all 0.2s ease",
                                }}
                              >
                                <AddCircleIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleRadioisotopeView(discipline)
                                }
                                title="View Radioisotopes/Test Kits"
                                sx={{
                                  width: 32,
                                  height: 32,
                                  backgroundColor: "#e8f5e8",
                                  color: "#2e7d32",
                                  "&:hover": {
                                    backgroundColor: "#c8e6c9",
                                    transform: "scale(1.05)",
                                  },
                                  transition: "all 0.2s ease",
                                }}
                              >
                                <ViewIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </>
                      )}
                      {!isSubUser &&
                        (applicationStage === null ||
                          applicationStage === "" ||
                          applicationStage === "18a") && (
                          <TableCell align="center">
                            <IconButton
                              onClick={() => handleDeleteDiscipline(discipline)}
                              title="Delete Discipline"
                              sx={{
                                width: 36,
                                height: 36,
                                backgroundColor: "#ffebee",
                                color: "#d32f2f",
                                "&:hover": {
                                  backgroundColor: "#ffcdd2",
                                  transform: "scale(1.05)",
                                },
                                transition: "all 0.2s ease",
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </TableCell>
                        )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={cancelDelete}
          aria-labelledby="delete-dialog-title"
          aria-describedby="delete-dialog-description"
        >
          <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
          <DialogContent>
            <DialogContentText id="delete-dialog-description">
              Are you sure you want to remove the discipline "
              {disciplineToDelete?.DisciplineName}" from your application? This
              action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={cancelDelete} color="primary">
              Cancel
            </Button>
            <Button
              onClick={confirmDeleteDiscipline}
              color="error"
              variant="contained"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Notification Snackbar */}
        <Snackbar
          open={notification.open}
          autoHideDuration={6000}
          onClose={closeNotification}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={closeNotification}
            severity={notification.severity}
            sx={{ width: "100%" }}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      </Box>
    );
};

export default DisciplineSection;
